import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { RootState } from "@/app/rootReducer";
import { PaginationState } from "@/components/Datatable/components/DataTablePagination/types";
import { OnChangeFn, SortingState } from "@/components/Datatable/types";
import { useNotification } from "@/context/NotificationContext";
import {
  GetStagesRequest,
  Stage,
  StageStatusFilter,
} from "../api/getStages/types";
import { StageFormValues } from "../components/StageForm/StageForm";
import {
  createStageAction,
  deleteStageAction,
  getStagesAction,
  updateStageAction,
} from "../slice/stageSlice";

export const useStages = () => {
  const dispatch = useAppDispatch();
  const { showNotification } = useNotification();

  const stages = useAppSelector((state: RootState) => state.stage.stages);
  const totalCount = useAppSelector(
    (state: RootState) => state.stage.totalCount,
  );
  const loading = useAppSelector((state: RootState) => state.stage.loadingList);
  const error = useAppSelector((state: RootState) => state.stage.listError);
  const loadingCreate = useAppSelector(
    (state: RootState) => state.stage.loadingCreate,
  );
  const loadingUpdate = useAppSelector(
    (state: RootState) => state.stage.loadingUpdate,
  );
  const loadingDelete = useAppSelector(
    (state: RootState) => state.stage.loadingDelete,
  );

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [status, setStatus] = useState<StageStatusFilter>("all");

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingStage, setEditingStage] = useState<Stage | null>(null);
  const [viewingStage, setViewingStage] = useState<Stage | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Stage | null>(null);

  const requestData = useMemo<GetStagesRequest>(
    () => ({
      searchTerm: searchTerm || undefined,
      status,
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      sortBy: sorting.length
        ? (sorting[0].id as "name" | "description")
        : undefined,
      sortOrder: sorting.length
        ? sorting[0].desc
          ? "DESC"
          : "ASC"
        : undefined,
    }),
    [searchTerm, status, pagination, sorting],
  );

  const refetch = useCallback(() => {
    dispatch(getStagesAction(requestData));
  }, [dispatch, requestData]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const handlePaginationChange: OnChangeFn<PaginationState> = (
    updaterOrValue,
  ) => {
    setPagination((old) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(old)
        : updaterOrValue,
    );
  };

  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    setSorting((old) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(old)
        : updaterOrValue,
    );
  };

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleStatusChange = useCallback((nextStatus: StageStatusFilter) => {
    setStatus(nextStatus);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const openCreate = useCallback(() => setIsCreateOpen(true), []);
  const closeCreate = useCallback(() => setIsCreateOpen(false), []);

  const openEdit = useCallback((stage: Stage) => setEditingStage(stage), []);
  const closeEdit = useCallback(() => setEditingStage(null), []);

  const openView = useCallback((stage: Stage) => setViewingStage(stage), []);
  const closeView = useCallback(() => setViewingStage(null), []);

  const editFromView = useCallback((stage: Stage) => {
    setViewingStage(null);
    setEditingStage(stage);
  }, []);

  const requestDelete = useCallback(
    (stage: Stage) => setDeleteTarget(stage),
    [],
  );
  const closeDelete = useCallback(() => setDeleteTarget(null), []);

  const toPayload = (values: StageFormValues) => ({
    name: values.name,
    position: Number(values.position),
    description: values.description.trim() ? values.description : null,
  });

  const submitCreate = useCallback(
    (values: StageFormValues) => {
      dispatch(
        createStageAction(toPayload(values), () => {
          showNotification("Success", "Stage created successfully.", "success");
          setIsCreateOpen(false);
          refetch();
        }),
      );
    },
    [dispatch, refetch, showNotification],
  );

  const submitEdit = useCallback(
    (values: StageFormValues) => {
      if (!editingStage) {
        return;
      }
      dispatch(
        updateStageAction(editingStage.uuid, toPayload(values), () => {
          showNotification("Success", "Stage updated successfully.", "success");
          setEditingStage(null);
          refetch();
        }),
      );
    },
    [dispatch, editingStage, refetch, showNotification],
  );

  const confirmDelete = useCallback(() => {
    if (!deleteTarget) {
      return;
    }
    dispatch(
      deleteStageAction(deleteTarget.uuid, () => {
        showNotification(
          "Stage Deleted",
          "The stage was deleted successfully.",
          "success",
        );
        setDeleteTarget(null);
        refetch();
      }),
    );
  }, [dispatch, deleteTarget, refetch, showNotification]);

  return {
    stages,
    totalCount,
    loading,
    error,
    pagination,
    sorting,
    searchTerm,
    status,
    handlePaginationChange,
    handleSortingChange,
    handleSearchChange,
    handleStatusChange,

    loadingCreate,
    loadingUpdate,
    loadingDelete,

    isCreateOpen,
    openCreate,
    closeCreate,

    editingStage,
    openEdit,
    closeEdit,

    viewingStage,
    openView,
    closeView,
    editFromView,

    deleteTarget,
    requestDelete,
    closeDelete,

    submitCreate,
    submitEdit,
    confirmDelete,
  };
};
